import { useSidebarStore } from "@/atoms/playground";
import { Box, Typography } from "@mui/material";
import { ExternalLink, PanelsTopLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import { CONFIDENTIAL_ROUTE_ID } from "@/atoms/confidential-session";

export const PlaygroundNavbar = ({
  title,
  id,
  actions,
}: {
  title: string;
  id: string;
  actions?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const sidebarState = useSidebarStore();

  const handleToggleSidebar = () => {
    sidebarState.toggle();
  };
  const location = useLocation();
  const path = location.pathname.split("/");
  const storedTheme = useStoredTheme();
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "absolute",
        top: "0",
        insetInlineStart: "0",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: { xs: "10px 10px 20px 10px", sm: "16px 16px 24px 16px" },
        background:
          "linear-gradient(180deg, var(--card-background) 40%, transparent 100%)",
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          flex: 1,
          minWidth: 0,
          overflow: "hidden", // Ensures internal truncation works
          marginInlineEnd: 2,
        }}
      >
        {!sidebarState.isOpen && (
          <button
            aria-label={t("playground.openSidebar")}
            onClick={handleToggleSidebar}
            className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-[var(--bg)] hover:bg-[var(--hover-bg)] transition-colors"
            type="button"
            style={
              {
                "--bg":
                  storedTheme.button.primary.backgroundColor[
                    theme.palette.mode
                  ],
                "--hover-bg":
                  storedTheme.button.primary.hoverBackgroundColor[
                    theme.palette.mode
                  ],
              } as React.CSSProperties
            }
          >
            <PanelsTopLeft size={18} />
          </button>
        )}
        <Typography
          sx={{
            color: storedTheme.header.textColor[theme.palette.mode],
            fontWeight: "500",
            fontSize: { xs: "14px", sm: "16px" },
          }}
          variant="h1"
          component="h2"
          noWrap
        >
          {title}
        </Typography>
      </Box>

      <div className="flex items-center gap-3 shrink-0">
        {actions}
        {path[1] === "playground" && (
          <Link
            to={`/chat-standalone/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-confidential-leave-guard={
              id === CONFIDENTIAL_ROUTE_ID ? "true" : undefined
            }
            aria-label={t("playground.openInNewTab")}
          >
            <div
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-[var(--bg)] hover:bg-[var(--hover-bg)]"
              style={
                {
                  padding: "4px",
                  "--bg":
                    storedTheme.button.primary.backgroundColor[
                      theme.palette.mode
                    ],
                  "--hover-bg":
                    storedTheme.button.primary.hoverBackgroundColor[
                      theme.palette.mode
                    ],
                } as React.CSSProperties
              }
            >
              <ExternalLink
                color={
                  storedTheme.button.primary.textColor[theme.palette.mode]
                }
              />
            </div>
          </Link>
        )}
      </div>
    </Box>
  );
};
